import { BreakpointKey } from '../../../theming';
import { ImageCropParamKeys, ImageParams, ResponsiveCropDimensions } from '../ImageGallery.types';

/**
 * Builds a set of responsive image urls from a base image URL and crop dimensions per breakpoint.
 * Appends query params for width and height using configurable keys, along with any additional parameters.
 *
 * The resulting URLs are constructed by appending the width and height parameters (e.g. ?w=...&h=...)
 * or their equivalents per the specified param keys (e.g. ?width=...&height=...)
 * and any other given query parameters (e.g. quality, fit).
 *
 * @param baseUrl - The base image URL without query parameters
 * @param cropDims - An object mapping breakpoint keys to { w, h } crop sizes
 * @param cropParamKeys - Optional object specifying query parameter keys for width and height
 *                        (default: { widthKey: 'w', heightKey: 'h' })
 * @param additionalCropParams - Optional object of additional query parameters to append to each URL
 * @returns ImageSet object mapping each breakpoint key to its cropped image URL with appropriate query parameters
 */
export function buildResponsiveImageSet(
    baseUrl: string,
    cropDims: ResponsiveCropDimensions,
    cropParamKeys: ImageCropParamKeys = { widthKey: 'w', heightKey: 'h' },
    additionalCropParams: ImageParams = {}
): { [key in BreakpointKey]?: string } {
    return Object.fromEntries(
        Object.entries(cropDims)
            .filter(([, dims]) => dims !== undefined)
            .map(([bp, dims]) => {
                const breakpoint = bp as BreakpointKey;
                const separator = baseUrl.includes('?') ? '&' : '?';

                const widthParam = dims.w ? `${cropParamKeys.widthKey}=${dims.w}` : null;
                const heightParam = dims.h ? `${cropParamKeys.heightKey}=${dims.h}` : null;

                const additionalParamsString = Object.entries(additionalCropParams)
                    .map(([key, val]) => `${encodeURIComponent(key)}=${encodeURIComponent(val)}`)
                    .join('&');
                const params = [widthParam, heightParam, additionalParamsString].filter(Boolean).join('&');

                return [breakpoint, `${baseUrl}${separator}${params}`];
            })
    );
}
